/**
 * Catalogue descriptions arrive as HTML fragments — mostly one or more
 * `<ul>` of facts, sometimes `<p>` or `<br>`-separated prose, occasionally
 * plain text. The dialog must not inject that markup (the source is a
 * supplier feed, not our own copy), so this turns it into plain strings:
 * every `<li>` becomes a bullet, everything outside lists a paragraph.
 * Tags are stripped, entities decoded, whitespace collapsed, empties dropped.
 */
export interface ParsedDescription {
  paragraphs: string[];
  bullets: string[];
}

const EMPTY: ParsedDescription = { paragraphs: [], bullets: [] };

/** Something that looks like a tag start: `<` followed by a letter, `/` or `!`. */
const TAG_LIKE = /<[a-z/!]/i;

/** Paragraph separator both paths use; a NUL never occurs in a description. */
const BREAK = '\u0000';

/** Elements whose text is code or hidden, never product facts. */
const HIDDEN_ELEMENTS = 'script, style, template, noscript';

/** Tags that end a run of text; a boundary becomes a paragraph break. */
const BLOCK_TAGS = new Set([
  'P',
  'DIV',
  'BR',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'TR',
  'TABLE',
  'SECTION',
  'ARTICLE',
  'HEADER',
  'FOOTER',
  'BLOCKQUOTE',
  'PRE',
  'DL',
  'DT',
  'DD',
  'HR',
]);

/**
 * The handful of named entities supplier feeds actually use. Numeric
 * references are decoded generically; unknown names stay as they are rather
 * than vanish.
 */
const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  sup2: '²',
  sup3: '³',
  deg: '°',
  euro: '€',
  auml: 'ä',
  ouml: 'ö',
  uuml: 'ü',
  Auml: 'Ä',
  Ouml: 'Ö',
  Uuml: 'Ü',
  szlig: 'ß',
  eacute: 'é',
  egrave: 'è',
  ndash: '–',
  mdash: '—',
  hellip: '…',
  laquo: '«',
  raquo: '»',
  bdquo: '„',
  ldquo: '“',
  rdquo: '”',
  lsquo: '‘',
  rsquo: '’',
  middot: '·',
  bull: '•',
  times: '×',
  reg: '®',
  copy: '©',
  trade: '™',
};

function decodeEntities(text: string): string {
  return text.replace(
    /&(#x[0-9a-f]+|#\d+|[a-z][a-z0-9]*);/gi,
    (match, body: string) => {
      if (body[0] === '#') {
        const code =
          body[1] === 'x' || body[1] === 'X'
            ? parseInt(body.slice(2), 16)
            : parseInt(body.slice(1), 10);
        return Number.isFinite(code) && code > 0 && code <= 0x10ffff
          ? String.fromCodePoint(code)
          : match;
      }
      return NAMED_ENTITIES[body] ?? match;
    }
  );
}

/** Collapses every run of whitespace (including `&nbsp;`) to one space. */
function tidy(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

function tidyAll(items: string[]): string[] {
  return items.map(tidy).filter((item) => item.length > 0);
}

/** Plain text keeps its blank lines as paragraph breaks and nothing else. */
function parsePlainText(text: string): ParsedDescription {
  return {
    paragraphs: tidyAll(decodeEntities(text).split(/\n\s*\n/)),
    bullets: [],
  };
}

function parseWithDom(html: string): ParsedDescription {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const body = doc.body;
  body.querySelectorAll(HIDDEN_ELEMENTS).forEach((el) => el.remove());
  // `textContent` glues adjacent cells together ("Gewicht180 g"); a space
  // after each cell keeps the words apart, and `tidy` drops the surplus.
  body.querySelectorAll('td, th').forEach((cell) => cell.append(' '));

  // Each <li> contributes its own text only; nested lists are visited as
  // items of their own, so the order stays the document order. A line
  // break inside an item is a word boundary, not a bullet of its own.
  const bullets = Array.from(body.querySelectorAll('li'), (li) => {
    const own = li.cloneNode(true) as HTMLElement;
    own.querySelectorAll('ul, ol').forEach((el) => el.remove());
    own.querySelectorAll('br').forEach((br) => br.replaceWith(' '));
    return own.textContent ?? '';
  });
  body.querySelectorAll('ul, ol, li').forEach((el) => el.remove());

  // Line breaks in the source are mere whitespace; only block boundaries
  // break, marked with a character that cannot occur in the text.
  const parts: string[] = [];
  const collect = (node: Node) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        parts.push((child.textContent ?? '').replace(/\s+/g, ' '));
        return;
      }
      if (child.nodeType !== Node.ELEMENT_NODE) return;
      const isBlock = BLOCK_TAGS.has((child as Element).tagName);
      if (isBlock) parts.push(BREAK);
      collect(child);
      if (isBlock) parts.push(BREAK);
    });
  };
  collect(body);

  return {
    paragraphs: tidyAll(parts.join('').split(BREAK)),
    bullets: tidyAll(bullets),
  };
}

/**
 * A list item runs from `<li>` to the next list tag of any kind, so a nested
 * list ends the parent's own text and its items follow as their own matches.
 */
const LIST_ITEM = /<li\b[^>]*>([\s\S]*?)(?=<\/?(?:li|ul|ol)\b|$)/gi;
const HIDDEN_BLOCK =
  /<(script|style|template|noscript)\b[^>]*>[\s\S]*?<\/\1\s*>/gi;
const BLOCK_BOUNDARY =
  /<\/?(?:p|div|br|h[1-6]|tr|table|section|article|header|footer|blockquote|pre|dl|dt|dd|hr)\b[^>]*\/?>/gi;
const ANY_TAG = /<[^>]+>/g;
/** A line break inside a list item: a word boundary, not a bullet. */
const LINE_BREAK = /<br\b[^>]*>/gi;
/** The end of a table cell: the next cell's text must not glue onto it. */
const CELL_END = /<\/t[dh]\b[^>]*>/gi;

function stripTags(fragment: string): string {
  return decodeEntities(fragment.replace(ANY_TAG, ''));
}

/** Same result as the DOM path for the markup the catalogue produces. */
function parseWithRegex(html: string): ParsedDescription {
  const visible = html.replace(HIDDEN_BLOCK, '').replace(CELL_END, ' ');
  const bullets = Array.from(visible.matchAll(LIST_ITEM), (m) =>
    stripTags((m[1] ?? '').replace(LINE_BREAK, ' '))
  );
  // Line breaks in the source are mere whitespace; only block tags break.
  const prose = visible
    .replace(LIST_ITEM, '')
    .replace(/\s+/g, ' ')
    .replace(BLOCK_BOUNDARY, BREAK);
  return {
    paragraphs: tidyAll(stripTags(prose).split(BREAK)),
    bullets: tidyAll(bullets),
  };
}

export function parseDescription(
  html: string | null | undefined
): ParsedDescription {
  if (!html || !html.trim()) return EMPTY;
  if (!TAG_LIKE.test(html)) return parsePlainText(html);
  return typeof DOMParser === 'function'
    ? parseWithDom(html)
    : parseWithRegex(html);
}
